import { lazy } from "react";
import { administratorRoleOnly, marketingAdministratorRoles } from "../../config/roles";
import { downloadAnnotation } from "../../utils/utility";
import { isPublished } from "./communicationRules";
import { PageAction, PageBadge, PageRecord } from "../recordDefinition.types";

// Lazy, as the other pages' are, so each modal loads only when it is opened.
const CommunicationCopyModal = lazy(() => import("./actions/CommunicationCopyModal"));
const CommunicationStatusModal = lazy(() => import("./actions/CommunicationStatusModal"));
const CommunicationStopStartModal = lazy(() => import("./actions/CommunicationStopStartModal"));
const CommunicationSaveLayoutModal = lazy(() => import("./actions/CommunicationSaveLayoutModal"));

const PublishedStatus = 4;

type Annotation = { Id?: string; FileSize?: number };

// A file the email service stored on the record, with something in it.
const fileOf = (record: PageRecord, key: string) => {
  const file = record[key] as Annotation | undefined;

  return file?.Id && Number(file.FileSize) > 0 ? file.Id : undefined;
};

const designZipOf = (record: PageRecord) => {
  const related = (record.relatedEntities ?? []) as {
    entityName: string;
    entities?: { Id: string; Fields?: { filename?: string; filesize?: number } }[];
  }[];
  const file = related
    .find((group) => group.entityName === "annotation")
    ?.entities?.find((entity) => entity.Fields?.filename === "communication-template-design.zip");

  return file && Number(file.Fields?.filesize) > 0 ? file.Id : undefined;
};

// The files are for support, so they are offered only when the URL asks for debug.
const debugging = () => new URLSearchParams(window.location.search).get("debug") === "true";

const download = (id: string, label: string, idOf: (record: PageRecord) => string | undefined): PageAction => ({
  id,
  label,
  roles: administratorRoleOnly,
  visible: (record) => debugging() && Boolean(record.id) && Boolean(idOf(record)),
  run: async (record) => {
    await downloadAnnotation(idOf(record));

    return undefined;
  },
});

export const communicationPageActions: PageAction[] = [
  {
    id: "copy",
    label: "Copy Communication",
    roles: marketingAdministratorRoles,
    modal: CommunicationCopyModal,
  },
  {
    id: "stop-start",
    label: (record) => (record.g4c_active ? "Stop Communication" : "Start Communication"),
    roles: marketingAdministratorRoles,
    visible: (record) => Boolean(record.id) && record.statuscode === PublishedStatus,
    modal: CommunicationStopStartModal,
  },
  {
    id: "set-status",
    label: (record) => (record.statecode === 0 ? "Deactivate Communication" : "Activate Communication"),
    roles: marketingAdministratorRoles,
    modal: CommunicationStatusModal,
  },
  {
    id: "save-layout",
    label: "Save as Layout",
    roles: marketingAdministratorRoles,
    visible: (record) => Array.isArray(record.rows) && record.rows.length > 0,
    modal: CommunicationSaveLayoutModal,
  },
  download("download-design", "Download Communication Template Design", designZipOf),
  download("download-preview", "Download Preview", (record) => fileOf(record, "preview.zip")),
  download("download-template-data", "Download Template Data", (record) => fileOf(record, "templateDataAnnotation")),
];

export const communicationPageBadges: PageBadge[] = [
  {
    id: "sid",
    label: (record) => String(record.g4c_sid),
    variant: () => "info",
    visible: isPublished,
  },
  {
    id: "state",
    label: (record) => (record.statecode === 0 ? "Active" : "Inactive"),
    variant: (record) => (record.statecode === 0 ? "success" : "warning"),
  },
];
